/**
 * storable.ts — localStorage-backed Svelte writable store.
 *
 * Creates a writable store whose value is automatically persisted to
 * localStorage. On creation the store reads any existing value from
 * localStorage and falls back to the provided default.
 */

import { writable, get, type Writable, type Updater } from 'svelte/store';

/**
 * Create a Svelte writable store that syncs its value to localStorage.
 *
 * @param key - localStorage key.
 * @param data - Default value when no stored value exists.
 * @param storeAsJson - If true (default), values are JSON-serialized. Set to false for plain strings.
 * @returns A writable store backed by localStorage.
 */
export function storable<T>(key: string, data: T, storeAsJson: boolean = true): Writable<T> {
	const store: Writable<T> = writable(data);

	/* Hydrate from localStorage if a value exists. */
	if (localStorage.getItem(key) != null) {
		store.set(storeAsJson ? JSON.parse(localStorage.getItem(key)!) : (localStorage.getItem(key) as T));
	} else {
		store.set(data);
	}

	return {
		subscribe: store.subscribe,
		set: (value: T) => {
			localStorage.setItem(key, storeAsJson ? JSON.stringify(value) : (value as string));
			store.set(value);
		},
		update: (fn: Updater<T>) => {
			const updated: T = fn(get(store));
			localStorage.setItem(key, storeAsJson ? JSON.stringify(updated) : (updated as string));
			store.set(updated);
		}
	};
}
